import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Grid,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Alert
} from '@mui/material';
import {
  Business,
  People,
  Groups,
  EventSeat,
  TrendingUp,
  LocationOn,
  CheckCircle,
  Block,
  Refresh,
  AccessTime,
  EventNote,
  QrCodeScanner,
  ChevronRight,
  AccountBalanceWallet,
  Language
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Home - Mobile-First Dashboard Overview
 */
function LibrarianHome({ showSnackbar }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [library, setLibrary] = useState(null);
  const [students, setStudents] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned to your account.', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);

      // Fetch students
      const studentsResponse = await api.get('/librarian/students');
      setStudents(studentsResponse.data.data.students || []);

      // Fetch active sessions
      try {
        const sessionsResponse = await api.get('/librarian/sessions', {
          params: { library_id: myLibrary.id, active: true }
        });
        setActiveSessions(sessionsResponse.data.data.sessions || []);
      } catch (err) {
        setActiveSessions([]);
      }

      // Fetch upcoming bookings
      try {
        const bookingsResponse = await api.get('/bookings', {
          params: { library_id: myLibrary.id }
        });
        const bookings = bookingsResponse.data.data.bookings || [];
        const upcoming = bookings.filter(b =>
          b.status === 'active' || b.status === 'upcoming'
        );
        setUpcomingBookings(upcoming.slice(0, 5));
      } catch (err) {
        setUpcomingBookings([]);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      showSnackbar(err.response?.data?.message || 'Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const occupancyRate = library?.capacity ? Math.round((activeSessions.length / library.capacity) * 100) : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Dashboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Welcome back, {user?.name}!
                </Typography>
              </Box>
              <IconButton
                onClick={fetchDashboardData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Library Card */}
          <MotionCard
            variants={itemVariants}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 2,
              background: GRADIENTS.navyLight,
              color: 'white',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            {/* Background Pattern */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0.1,
                backgroundImage: `radial-gradient(circle at 20px 20px, white 2px, transparent 0)`,
                backgroundSize: '40px 40px'
              }}
            />

            <CardContent sx={{ p: 3, position: 'relative' }}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Business sx={{ fontSize: 30 }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight="700" gutterBottom>
                    {library?.name || 'Your Library'}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <LocationOn fontSize="small" sx={{ opacity: 0.9 }} />
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      {library?.address?.split(',')[0] || 'Address'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: RADIUS.md,
                  p: 2
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Status
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {library?.active ? '🟢 Active' : '🔴 Inactive'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ opacity: 0.9 }}>
                      Capacity
                    </Typography>
                    <Typography variant="body1" fontWeight="700">
                      {library?.capacity || 0} Seats
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Stats Cards */}
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.brand,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Active Now
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {activeSessions.length}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        students
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <Groups />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.coin,
                  color: 'white'
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Occupancy
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {occupancyRate}%
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        capacity
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <TrendingUp />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                onClick={() => navigate('/librarian/students')}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: 'linear-gradient(135deg, #4A90A4 0%, #2D6B7D 100%)',
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Total Students
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {students.length}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        registered
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <People />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>

            <Grid item xs={6}>
              <MotionCard
                variants={itemVariants}
                onClick={() => navigate('/librarian/bookings')}
                sx={{
                  borderRadius: RADIUS.lg,
                  background: GRADIENTS.navyLight,
                  color: 'white',
                  cursor: 'pointer',
                  '&:hover': { transform: 'scale(1.02)', transition: 'transform 0.2s' }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>
                        Bookings
                      </Typography>
                      <Typography variant="h4" fontWeight="700">
                        {upcomingBookings.length}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>
                        upcoming
                      </Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 40, height: 40 }}>
                      <EventNote />
                    </Avatar>
                  </Box>
                </CardContent>
              </MotionCard>
            </Grid>
          </Grid>

          {/* Quick Actions */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="700" mb={2}>
                Quick Actions
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<QrCodeScanner />}
                    onClick={() => navigate('/librarian/scanner')}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Scan QR
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<People />}
                    onClick={() => navigate('/librarian/students')}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Students
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AccountBalanceWallet />}
                    onClick={() => navigate('/librarian/wallet')}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    My Earnings
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Language />}
                    onClick={() => navigate('/librarian/website')}
                    sx={{
                      borderRadius: RADIUS.md,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Website
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </MotionCard>

          {/* Active Sessions Preview */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Groups color="primary" />
                  <Typography variant="subtitle1" fontWeight="700">
                    Active Sessions
                  </Typography>
                </Box>
                <Chip
                  label={`${activeSessions.length} Active`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Divider />
              {activeSessions.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    No active sessions at the moment
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {activeSessions.slice(0, 3).map((session, index) => (
                    <React.Fragment key={session.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              background: GRADIENTS.brand
                            }}
                          >
                            {session.student?.name?.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={session.student?.name || 'Unknown'}
                          secondary={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" />
                              {new Date(session.entered_at).toLocaleTimeString()}
                            </Box>
                          }
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        <Chip
                          label="In Session"
                          color="success"
                          size="small"
                          icon={<CheckCircle />}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                  {activeSessions.length > 3 && (
                    <>
                      <Divider />
                      <ListItem
                        button
                        onClick={() => showSnackbar('Navigate to Sessions tab', 'info')}
                        sx={{ justifyContent: 'center', py: 1 }}
                      >
                        <Typography variant="body2" color="primary" fontWeight="600">
                          View All {activeSessions.length} Sessions
                        </Typography>
                        <ChevronRight color="primary" fontSize="small" />
                      </ListItem>
                    </>
                  )}
                </List>
              )}
            </CardContent>
          </MotionCard>

          {/* Upcoming Bookings Preview */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl }}>
            <CardContent sx={{ p: 0 }}>
              <Box p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <EventNote color="primary" />
                  <Typography variant="subtitle1" fontWeight="700">
                    Upcoming Bookings
                  </Typography>
                </Box>
                <Chip
                  label={`${upcomingBookings.length} Total`}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Divider />
              {upcomingBookings.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    No upcoming bookings
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {upcomingBookings.slice(0, 3).map((booking, index) => (
                    <React.Fragment key={booking.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              background: GRADIENTS.navyLight
                            }}
                          >
                            {booking.student?.name?.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={booking.student?.name || 'Unknown'}
                          secondary={booking.plan?.name || 'N/A'}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {new Date(booking.start_date).toLocaleDateString('en-IN', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </Typography>
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibrarianHome;
